import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Plus,
  Copy,
  Languages,
  Archive,
  Trash2,
  Pencil,
  CheckCircle2,
  Clock,
  Globe,
  BookOpen,
  Users,
  MoreVertical,
} from "lucide-react";
import { useGlobalState } from "../../GlobalStates";
import Modal from "../../components/common/Modal";
import { listCourses, createCourse, duplicateCourse, archiveCourse, deleteCourse } from "../../services/courseService";

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "zh-Hant-TW", label: "Traditional Chinese (TW)" },
  { value: "zh-Hans-CN", label: "Simplified Chinese (CN)" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "th", label: "Thai" },
  { value: "vi", label: "Vietnamese" },
];

// Map locale values to course language codes
const LOCALE_TO_LANGUAGE = {
  en: "en",
  "zh-Hant-TW": "zh-Hant-TW",
  "zh-Hans-CN": "zh-Hans-CN",
  ja: "ja",
  ko: "ko",
  th: "th",
  vi: "vi",
};

const StatusBadge = ({ status }) => {
  const styles = {
    published: "tw-bg-green-100 tw-text-green-700",
    draft: "tw-bg-yellow-100 tw-text-yellow-700",
    archived: "tw-bg-gray-100 tw-text-gray-500",
  };
  return (
    <span className={`tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium ${styles[status] || styles.draft}`}>
      {status}
    </span>
  );
};

const AdminActionsMenu = ({ onEdit, onEnrollments, onDuplicate, onTranslate, onArchive, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [open]);

  const handleAction = (fn) => {
    fn?.();
    setOpen(false);
  };

  const items = [
    { icon: Pencil, label: "Edit", onClick: () => handleAction(onEdit), danger: false },
    { icon: Users, label: "Enrollments", onClick: () => handleAction(onEnrollments), danger: false },
    { icon: Copy, label: "Duplicate", onClick: () => handleAction(onDuplicate), danger: false },
    { icon: Languages, label: "Translate", onClick: () => handleAction(onTranslate), danger: false },
    { icon: Archive, label: "Archive", onClick: () => handleAction(onArchive), danger: true },
    { icon: Trash2, label: "Delete", onClick: () => handleAction(onDelete), danger: true },
  ];

  return (
    <div className="tw-relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="tw-rounded-lg tw-p-2 tw-text-gray-400 tw-transition-colors hover:tw-bg-gray-100 hover:tw-text-gray-600"
        title="Actions"
      >
        <MoreVertical className="tw-h-4 tw-w-4" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="tw-absolute tw-right-0 tw-top-full tw-z-10 tw-mt-1 tw-min-w-[160px] tw-overflow-hidden tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-py-1 tw-shadow-lg"
          >
            {items.map(({ icon: Icon, label, onClick, danger }) => (
              <button
                key={label}
                onClick={(e) => { e.stopPropagation(); onClick(); }}
                className={`tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-text-left tw-text-sm tw-transition-colors hover:tw-bg-gray-50 ${
                  danger ? "tw-text-red-600 hover:tw-bg-red-50" : "tw-text-gray-700"
                }`}
              >
                <Icon className="tw-h-4 tw-w-4 tw-shrink-0" />
                {label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const EnrollmentBadge = ({ status, score }) => {
  if (status === "completed") {
    return (
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-green-600">
        <CheckCircle2 className="tw-h-4 tw-w-4" />
        <span className="tw-text-xs tw-font-medium">Completed{score != null ? ` (${score}%)` : ""}</span>
      </div>
    );
  }
  if (status === "in_progress") {
    return (
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-yellow-600">
        <Clock className="tw-h-4 tw-w-4" />
        <span className="tw-text-xs tw-font-medium">In Progress</span>
      </div>
    );
  }
  return null;
};

const ConfirmModal = ({ open, onClose, onConfirm, title, message, confirmLabel = "Confirm", confirmColor = "tw-bg-red-500 hover:tw-bg-red-600", loading = false }) => (
  <Modal open={open} onClose={onClose} title={title} maxWidth="sm" maxHeight="auto">
    <p className="tw-text-sm tw-text-gray-600">{message}</p>
    <div className="tw-mt-6 tw-flex tw-gap-3">
      <button
        onClick={onClose}
        className="tw-flex-1 tw-rounded-xl tw-border tw-border-gray-200 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-transition-all hover:tw-bg-gray-50"
      >
        Cancel
      </button>
      <button
        onClick={onConfirm}
        disabled={loading}
        className={`tw-flex-1 tw-rounded-xl tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-transition-all disabled:tw-opacity-50 ${confirmColor}`}
      >
        {loading ? "Processing..." : confirmLabel}
      </button>
    </div>
  </Modal>
);

// Slug generator helper
const generateSlug = (title) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const CreateCourseModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ title: "", slug: "", description: "", language: "en" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setForm({ title: "", slug: "", description: "", language: "en" });
    setError("");
    onClose();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setForm((f) => ({ ...f, title, slug: generateSlug(title) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.title.trim()) {
      setError("Title is required");
      return;
    }
    const slugToUse = form.slug || generateSlug(form.title);
    setLoading(true);
    try {
      const { data } = await createCourse({ ...form, slug: slugToUse, status: "published" });
      onCreated(data);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create course");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Create new course" maxWidth="md" maxHeight="auto">
      <form onSubmit={handleSubmit} className="tw-space-y-4">
        <div>
          <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">Title</label>
          <input
            type="text"
            value={form.title}
            onChange={handleTitleChange}
            className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-primary-main focus:tw-outline-none"
            placeholder="e.g. Junior Duck Leader Onboarding"
          />
        </div>
        <div>
          <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={2}
            className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-primary-main focus:tw-outline-none"
            placeholder="Short description..."
          />
        </div>
        <div>
          <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">Language</label>
          <select
            value={form.language}
            onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
            className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-primary-main focus:tw-outline-none"
          >
            {LANGUAGE_OPTIONS.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>
        {error && <p className="tw-text-sm tw-text-red-500">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="tw-w-full tw-rounded-xl tw-bg-primary-main tw-py-2.5 tw-font-medium tw-text-white tw-transition-all hover:tw-bg-primary-main/90 disabled:tw-opacity-50"
        >
          {loading ? "Creating..." : "Create course"}
        </button>
      </form>
    </Modal>
  );
};

const TranslateModal = ({ open, onClose, course, onTranslated }) => {
  const [targetLanguage, setTargetLanguage] = useState("zh-Hant-TW");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleTranslate = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await duplicateCourse(course.id, { targetLanguage, autoTranslate: true });
      onTranslated(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to translate course");
    } finally {
      setLoading(false);
    }
  };

  if (!course) return null;

  return (
    <Modal open={open} onClose={onClose} title="Translate course" maxWidth="md" maxHeight="auto">
      <p className="tw-mb-4 tw-text-sm tw-text-gray-500">
        This will create a new course translated from <strong>{course.title}</strong> ({course.language}) into the selected language.
      </p>
      <div className="tw-space-y-4">
        <div>
          <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">Target language</label>
          <select
            value={targetLanguage}
            onChange={(e) => setTargetLanguage(e.target.value)}
            className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-primary-main focus:tw-outline-none"
          >
            {LANGUAGE_OPTIONS.filter((l) => l.value !== course.language).map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>
        <p className="tw-text-xs tw-text-gray-400">
          All content (title, descriptions, questions, options) will be auto-translated. The new course will be created as published.
        </p>
        {error && <p className="tw-text-sm tw-text-red-500">{error}</p>}
        <button
          onClick={handleTranslate}
          disabled={loading}
          className="tw-w-full tw-rounded-xl tw-bg-primary-main tw-py-2.5 tw-font-medium tw-text-white tw-transition-all hover:tw-bg-primary-main/90 disabled:tw-opacity-50"
        >
          {loading ? "Translating..." : "Create translated course"}
        </button>
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const CourseList = () => {
  const navigate = useNavigate();
  const [user] = useGlobalState("user");
  const isAdmin = !!user?.is_admin;

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [translateTarget, setTranslateTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCourses = useCallback(async () => {
    try {
      const { data } = await listCourses();
      setCourses(data);
    } catch (err) {
      console.error("Failed to fetch courses:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // Filter courses by user's preferred language for non-admin users
  const filteredCourses = useMemo(() => {
    if (isAdmin) return courses;

    const userLocale = localStorage.getItem("locale") || "en";
    const userLanguage = LOCALE_TO_LANGUAGE[userLocale] || "en";

    // Group courses by base_course_id to find translations
    const baseGroups = {};
    const standalone = [];

    courses.forEach((course) => {
      const baseId = course.base_course_id || course.id;
      if (!baseGroups[baseId]) baseGroups[baseId] = [];
      baseGroups[baseId].push(course);
    });

    // For each group, pick the course matching user's language, or fall back to the first available
    // Propagate group_completed status across the group so the selected course shows completion
    Object.values(baseGroups).forEach((group) => {
      const match = group.find((c) => c.language === userLanguage);
      const selected = match || group.find((c) => c.language === "en") || group[0];

      // If any course in the group is completed (via group_completed flag from BE),
      // reflect that on the selected course
      if (selected.group_completed && selected.enrollment_status !== "completed") {
        selected.enrollment_status = "completed";
        selected.completed_via_sibling = true;
      }

      standalone.push(selected);
    });

    return standalone;
  }, [courses, isAdmin]);

  const handleDuplicate = async (course) => {
    try {
      await duplicateCourse(course.id, { autoTranslate: false });
      await fetchCourses();
    } catch (err) {
      console.error("Failed to duplicate:", err);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await archiveCourse(archiveTarget.id);
      setCourses((prev) => prev.map((c) => (c.id === archiveTarget.id ? { ...c, status: "archived" } : c)));
      setArchiveTarget(null);
    } catch (err) {
      console.error("Failed to archive:", err);
    } finally {
      setArchiving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCourse(deleteTarget.id);
      setCourses((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete:", err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-white">
        <div className="tw-w-full tw-max-w-4xl tw-space-y-4 tw-px-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="tw-h-32 tw-animate-pulse tw-rounded-xl tw-bg-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-gray-900">
      <div className="tw-container tw-mx-auto tw-max-w-5xl tw-px-4 tw-pb-12 tw-pt-24 md:tw-pb-20 md:tw-pt-28">
        <div className="tw-mb-8 tw-flex tw-items-center tw-justify-between">
          <div>
            <h1 className="tw-text-3xl tw-font-bold md:tw-text-4xl">Courses</h1>
            <p className="tw-mt-2 tw-text-gray-500">Learn and grow as a community leader</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowCreate(true)}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-bg-primary-main tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-transition-all hover:tw-bg-primary-main/90"
            >
              <Plus className="tw-h-4 tw-w-4" />
              New course
            </button>
          )}
        </div>

        {filteredCourses.length === 0 ? (
          <div className="tw-rounded-2xl tw-bg-gray-50 tw-py-16 tw-text-center">
            <BookOpen className="tw-mx-auto tw-mb-4 tw-h-12 tw-w-12 tw-text-gray-300" />
            <p className="tw-text-lg tw-font-medium tw-text-gray-500">No courses available yet</p>
            {isAdmin && (
              <button
                onClick={() => setShowCreate(true)}
                className="tw-mt-4 tw-text-sm tw-font-medium tw-text-primary-main hover:tw-underline"
              >
                Create your first course
              </button>
            )}
          </div>
        ) : (
          <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
            <AnimatePresence>
              {filteredCourses.map((course) => (
                <motion.div
                  key={course.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="tw-group tw-rounded-2xl tw-border tw-border-gray-100 tw-bg-white tw-p-6 tw-shadow-sm tw-transition-shadow hover:tw-shadow-md"
                >
                  <div className="tw-flex tw-flex-col tw-gap-4">
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                      <div className="tw-flex-1">
                        <div className="tw-mb-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                          <h3 className="tw-text-lg tw-font-semibold">{course.title}</h3>
                          {isAdmin && <StatusBadge status={course.status} />}
                          <span className="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-text-xs tw-text-gray-500">
                            <Globe className="tw-h-3 tw-w-3" />
                            {course.language}
                          </span>
                        </div>
                        {course.description && (
                          <p className="tw-mb-3 tw-text-sm tw-text-gray-500 tw-line-clamp-2">
                            {course.description.replace(/<[^>]*>/g, "")}
                          </p>
                        )}
                      </div>
                      {isAdmin && (
                        <AdminActionsMenu
                          onEdit={() => navigate(`/courses/${course.slug}/edit`)}
                          onEnrollments={() => navigate(`/courses/${course.slug}/enrollments`)}
                          onDuplicate={() => handleDuplicate(course)}
                          onTranslate={() => setTranslateTarget(course)}
                          onArchive={() => setArchiveTarget(course)}
                          onDelete={() => setDeleteTarget(course)}
                        />
                      )}
                    </div>

                    <div className="tw-flex tw-items-center tw-justify-between">
                      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                        <span className="tw-text-xs tw-text-gray-400">
                          {course.section_count || 0} sections &middot; {course.question_count || 0} questions
                        </span>
                        <EnrollmentBadge status={course.enrollment_status} score={course.score} />
                      </div>
                      <button
                        onClick={() => navigate(`/courses/${course.slug}`)}
                        className={`tw-flex tw-items-center tw-gap-1.5 tw-rounded-xl tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-transition-all ${
                          course.completed_via_sibling
                            ? "tw-bg-green-100 tw-text-green-700 hover:tw-bg-green-200"
                            : "tw-bg-primary-main tw-text-white hover:tw-bg-primary-main/90"
                        }`}
                      >
                        {course.completed_via_sibling
                          ? "Completed"
                          : course.enrollment_status === "completed"
                            ? "Review"
                            : course.enrollment_status === "in_progress"
                              ? "Resume"
                              : "Start"}
                        {!course.completed_via_sibling && <ArrowRight className="tw-h-4 tw-w-4" />}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      <CreateCourseModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => fetchCourses()}
      />
      <TranslateModal
        open={!!translateTarget}
        onClose={() => setTranslateTarget(null)}
        course={translateTarget}
        onTranslated={() => fetchCourses()}
      />
      <ConfirmModal
        open={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
        title="Archive course"
        message={`Are you sure you want to archive "${archiveTarget?.title}"? It will be hidden from learners.`}
        confirmLabel="Archive"
        loading={archiving}
      />
      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete course permanently"
        message={`Are you sure you want to permanently delete "${deleteTarget?.title}"? This cannot be undone. All sections, questions, and enrollments will be removed.`}
        confirmLabel="Delete permanently"
        loading={deleting}
      />
    </div>
  );
};

export default CourseList;
